import { For, Show, type Component, type JSX } from 'solid-js';
import { Button } from '~ui/button';
import { Text } from '~ui/text';
import { MoonIcon, SunIcon } from '~ui/icons';
import { theme, toggleTheme } from '~/stores/theme-store';
import { page, setPage } from '~/stores/nav-store';
import { PAGES, type Page } from '~/pages/registry';
import { accounts } from '~/stores/portfolio-store';
import { focusedAccountId, unfocusAccount } from '~/stores/accounts-view-store';

// The pages, across the top.
//
// One --app-row-height band, same as a table row or a pane toolbar, so the bar
// costs a single row of height and the pane below it starts on the same rhythm
// everything else follows.
//
// This was a left rail. Horizontally there is no reason to collapse it: a rail
// collapsed to save width, and a bar one row tall has nothing left to give — so
// the collapse state, its icons and the localStorage key that remembered it are
// gone rather than kept as a control that would do nothing.

const NavItem: Component<{ page: Page }> = (props) => {
    const active = () => page() === props.page.id;

    /**
     * What the page is currently looking at, appended to its own name.
     *
     * Accounts is the only page with a level below itself, and while you are in
     * one the tab is the honest place to say so — it is where you already look
     * to know where you are, and it means the way out is somewhere you would
     * think to click rather than a control the page had to grow.
     */
    // Only while you are actually on that page. The focus is remembered when
    // you leave — coming back puts you where you were — but a crumb still
    // reading "Accounts › TFSA" from over on Explore describes a screen that is
    // not in front of you.
    const focused = () => (props.page.id === 'accounts' && active() ? accounts().find((a) => a.id === focusedAccountId()) : undefined);

    // The underline runs under the whole crumb, because the whole crumb is the
    // tab. The fill does not: it marks where you are, and while you are a level
    // down that is the account, not the page — filling "Accounts" too would say
    // you are on both.
    return (
        <button
            type='button'
            title={props.page.label}
            onClick={() => setPage(props.page.id)}
            class={`flex h-row shrink-0 cursor-default items-stretch pl-2 text-sm ${focused() ? '' : 'gap-2'} ${active() ? 'border-b-3 border-foreground' : 'hover:bg-muted'} ${active() && focused() == null ? 'bg-muted-foreground/20 pr-2' : 'pr-2'}`}>
            {/* Everything up to the account name is one target: the icon, the
                page name and the separator. Only the text was clickable before,
                which is a few characters of hit area on a tab-sized control —
                and the icon plainly belongs to "go back to Accounts" too.

                A span rather than a nested button, which is not valid markup,
                stopping the event so it does not also re-run the tab's own
                click. */}
            <Show
                when={focused()}
                fallback={
                    <>
                        <span class='flex items-center'>{props.page.icon()}</span>
                        <span class='flex items-center'>{props.page.label}</span>
                    </>
                }>
                <span
                    class='-ml-2 flex items-center gap-2 self-stretch px-2 hover:bg-muted'
                    title='Back to every account'
                    onClick={(e) => {
                        e.stopPropagation();
                        unfocusAccount();
                    }}>
                    <span class='flex items-center'>{props.page.icon()}</span>
                    <span class='flex items-center'>{props.page.label}</span>
                    <Text tone='muted'>›</Text>
                </span>
                {/* Filled edge to edge — a fill inset from the band it sits in
                    reads as a chip on the tab rather than as the tab's own
                    state. Negative right margin swallows the button's padding so
                    it runs to the tab's edge. */}
                <span class='-mr-2 flex items-center self-stretch bg-muted-foreground/20 px-2'>{focused()!.label}</span>
            </Show>
        </button>
    );
};

const Control: Component<{ icon: JSX.Element; title: string; onClick: () => void }> = (props) => (
    <div class='flex h-row shrink-0 items-center'>
        <Button size='fill' icon={props.icon} title={props.title} onClick={props.onClick} />
    </div>
);

const Nav: Component = () => (
    <nav class='flex h-row shrink-0 items-center justify-between bg-background'>
        {/* Scrolls rather than wrapping to a second row: the bar is one band
            tall by definition, and a nav that grows taller as pages are added
            would shift every pane below it. */}
        <div class='flex min-w-0 flex-1 items-center overflow-x-auto scrollbar-hide'>
            <For each={PAGES}>{(p) => <NavItem page={p} />}</For>
        </div>

        <div class='flex h-row shrink-0 items-center'>
            <Control icon={theme() === 'dark' ? <SunIcon /> : <MoonIcon />} title='Toggle theme' onClick={toggleTheme} />
        </div>
    </nav>
);

export default Nav;
